import React, { useEffect, useMemo, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AppLayout from "../../components/app/AppLayout";
import {
  CLIENT_ACTIVITY_ROUTE,
  CLIENT_EXERCISE_HISTORY_ROUTE,
  CLIENT_HOME_ROUTE,
  CLIENT_PROFILE_ROUTE,
  CLIENT_SETTING_PROFILE_ROUTE,
  CLIENT_SETTING_ROUTE,
  GROUPS_ROUTE,
  HOME_ROUTE,
  MEAL_LOGGING_ROUTE,
  MESSAGES_ROUTE,
  PROGRAMS_ROUTE,
  SIGN_UP_CLIENT_COMPLETE,
} from "../../routes/routes";
import { groupNavigation } from "../../routes/coach/group/GroupNavigation";
import {
  ClientActivityStates,
  IsMobileAppModalShown,
  UserRole,
  defaultHomeScreenMessage,
} from "../../constants";
import {
  useCoachIsImpersonating,
  useCurrentUser,
  useCurrentUserRole,
} from "../../hooks/useCurrentUser";
import GroupContext, { TGroupRoute } from "../../contexts/GroupContext";
import { Skeleton } from "@mui/material";
import { GroupPostsAddButton } from "../../components/group-posts/GroupPostsAddButton";
import { programsNavigation } from "../../routes/client/programs/utils/ProgramsNavigation";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import { clientSettingsNavigation } from "../../routes/client/settings/utils/common";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CompactDateDialogContext from "../../contexts/CompactDateDialogContext";
import { getISODate } from "../../utils/date";
import { NewMessageAction } from "../../components/messages/NewMessageAction";
import { ClientProfileActions } from "../../components/screen/ClientProfileScreen";
import { useMediaMobile } from "../../hooks/useMediaMobile";
import { FinishWorkoutModalContext } from "../../contexts/FinishWorkoutModalContext";
import dayjs from "dayjs";
import ProgramsContext from "../../contexts/ClientProgramsContext";
import {
  ClientEnrollmentDto,
  EnrollmentsStatusCountDto,
} from "@growth-machine-llc/stridist-api-client";
import { MobileAppModalContext } from "../../contexts/MobileAppModalContext";
import { getActionForGroup } from "./app-layout-helpers/common";
import { isClientSignUpComplete } from "../../utils/user";
import { HeaderButton } from "../../components/button/HeaderButton";
import ExitWorkoutButton from "../../components/coach-programs/buttons/ExitWorkoutButton";
import { ChangeExerciseButton } from "../../components/client-exercise-history/ChangeExerciseButton";
import HistoryExerciseListModalContext from "../../contexts/HistoryExerciseListModalContext";

const DEFAULT_CONFIG = {
  title: undefined,
  subtitle: undefined,
  actions: [],
  tabsNavigation: undefined,
  appBar: true,
  appDrawer: true,
  filters: undefined,
  breadcrumbs: [{ name: "" }],
  disableSpeedDial: true,
  forceBackButton: false,
  appBarHideOnMobile: false,
};

const AppLayoutClient = () => {
  const location = useLocation();
  const { slug, program, date, component, status, recipient } = useParams();
  const user = useCurrentUser();
  const isCoachImpersonating = useCoachIsImpersonating();
  const { id: userId, role } = user;
  const workspace = useCurrentWorkspace();
  const navigate = useNavigate();
  const { unreadMessagesCount } = useCurrentUser();
  const isMobile = useMediaMobile("md", false);
  const { breakpoints } = useTheme();
  const mdSm = useMediaQuery(breakpoints.down("sm"));
  const downLg = useMediaQuery(breakpoints.down("lg"));
  const [searchParams] = useSearchParams();
  const isWorkout = searchParams.get("workout");

  date === ":date" &&
    navigate(CLIENT_HOME_ROUTE.replace(":date", getISODate()));

  const pathArr = location.pathname.split("/");
  const PATHNAME_FIRST_SECTION = "/" + pathArr[1];
  const PATHNAME_LAST_SECTION = "/" + pathArr[pathArr.length - 1];

  const FORMATTED_DATE = date && dayjs(date).format("MMMM DD, YYYY");

  // LOADING CONTEXT
  const [loading, setLoading] = useState(true);

  const loadingContextValue = useMemo(
    () => ({ loading, setLoading }),
    [loading],
  );

  // GROUP CONTEXT
  const groupContextLoadingState = {
    name: "...",
    totalMembersCount: undefined,
  };
  const [groupBriefInfo, setGroupBriefInfo] = useState<TGroupRoute>(
    groupContextLoadingState,
  );

  const groupContextValue = useMemo(
    () => ({
      groupBriefInfo,
      setGroupBriefInfo,
      resetGroupBriefInfo: () => setGroupBriefInfo(groupContextLoadingState),
    }),
    [groupBriefInfo],
  );

  const [programsData, setProgramsData] = useState<ClientEnrollmentDto[]>();
  const [enrollmentsCount, setEnrollmentsCount] =
    useState<EnrollmentsStatusCountDto>();

  const programsContextValue = useMemo(
    () => ({
      enrollmentsCount,
      setEnrollmentsCount,
    }),
    [enrollmentsCount],
  );
  // COMPACT DATA MODAL CONTEXT
  const [dateDialogOpen, setDateDialogOpen] = React.useState(false);

  const compactDateDialogContextValue = useMemo(
    () => ({ dateDialogOpen, setDateDialogOpen }),
    [dateDialogOpen],
  );

  // FINISH WORKOUT CONTEXT
  const [isFinishWorkoutModalOpen, setIsFinishWorkoutModalOpen] =
    React.useState(false);

  const finishWorkoutModalContextValue = useMemo(
    () => ({ isFinishWorkoutModalOpen, setIsFinishWorkoutModalOpen }),
    [isFinishWorkoutModalOpen],
  );
  const [isMobileAppModalShown, setIsMobileAppModalShown] =
    React.useState<boolean>(
      isCoachImpersonating
        ? false
        : window.localStorage.getItem(IsMobileAppModalShown) !== "false",
    );

  // EXERCISE HISTORY LIST MODAL

  const [exerciseListModalOpen, setExerciseListModalOpen] =
    React.useState(false);

  const exerciseListModalContextValue = useMemo(
    () => ({ exerciseListModalOpen, setExerciseListModalOpen }),
    [exerciseListModalOpen],
  );

  // CONFIGS
  const getConfig = () => {
    switch (PATHNAME_FIRST_SECTION) {
      case GROUPS_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: groupBriefInfo.name,
          tabsNavigation: groupNavigation(
            slug,
            role as UserRole,
            groupBriefInfo.totalMembersCount,
          ),
          breadcrumbs: [{ name: "Groups" }],
          actions: getActionForGroup({
            pathname: location.pathname,
            groupRouteQuery: groupBriefInfo,
            userId: userId,
            setManageGroupDialogOpen: () => {},
          }),
        };

      case PROGRAMS_ROUTE:
        switch (status) {
          case ClientActivityStates.START:
            return {
              ...DEFAULT_CONFIG,
              title: "Program",
              subtitle: "Start workout",
              appBar: !mdSm,
            };
          case ClientActivityStates.SUMMARY:
            return {
              ...DEFAULT_CONFIG,
              title: "Program",
              subtitle: "Workout summary",
              actions: [<ExitWorkoutButton />],
            };
          case ClientActivityStates.END:
            return {
              ...DEFAULT_CONFIG,
              title: "Program",
              subtitle: "End workout",
              appBar: !mdSm,
            };
          default:
            if (isWorkout)
              return {
                ...DEFAULT_CONFIG,
                title: "Programs",
                subtitle: "During workout",
              };
            if (program && date && component)
              return {
                ...DEFAULT_CONFIG,
                appBar: false,
                appDrawer: false,
              };
            return {
              ...DEFAULT_CONFIG,
              title: "Programs",
              tabsNavigation: programsNavigation(
                enrollmentsCount?.active,
                enrollmentsCount?.upcoming,
                enrollmentsCount?.past,
              ),
              breadcrumbs: [{ name: "Programs" }],
            };
        }
      case HOME_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: "Home",
          subtitle: workspace.message || defaultHomeScreenMessage,
          breadcrumbs: [{ name: "Home" }],
          disableSpeedDial: false,
        };

      case CLIENT_EXERCISE_HISTORY_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: "Exercise History",
          breadcrumbs: [{ name: "Exercise History" }],
          actions: downLg && [
            <ChangeExerciseButton
              useThemePalette={true}
              setExerciseListModalOpen={setExerciseListModalOpen}
            />,
          ],
        };

      case CLIENT_SETTING_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: "My Account",
          breadcrumbs: [{ name: "Settings" }],
          tabsNavigation: clientSettingsNavigation,
        };

      case CLIENT_ACTIVITY_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: "Activity",
          subtitle: "View and edit past responses.",
          breadcrumbs: [{ name: "Activity" }],
        };

      case MEAL_LOGGING_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: "Meals",
          subtitle: (
            <Box
              style={{ cursor: "pointer" }}
              onClick={() => setDateDialogOpen(true)}
            >
              {FORMATTED_DATE}
            </Box>
          ),
          breadcrumbs: [{ name: "Meals" }],
          forceBackButton: true,
        };

      case MESSAGES_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: !isMobile ? "Messages" : null,
          subtitle: !isMobile
            ? `${unreadMessagesCount} new message${
                unreadMessagesCount === 1 ? "" : "s"
              }`
            : null,
          breadcrumbs: [{ name: "Messages" }],
          actions: !isMobile && [<NewMessageAction Button={HeaderButton} />],
          appBarHideOnMobile: recipient ? true : false,
        };

      case CLIENT_PROFILE_ROUTE:
        return {
          ...DEFAULT_CONFIG,
          title: "Profile",
          breadcrumbs: [{ name: "Profile" }],
          actions: [
            ClientProfileActions(
              () => {
                setIsMobileAppModalShown(true);
                localStorage.setItem(IsMobileAppModalShown, "true");
              },
              () => navigate(CLIENT_SETTING_PROFILE_ROUTE),
              isMobile,
            ),
          ],
        };

      default:
        return { ...DEFAULT_CONFIG };
    }
  };

  const config = getConfig();

  useEffect(() => {
    if (user?.role === UserRole.CLIENT && !isClientSignUpComplete(user)) {
      navigate(SIGN_UP_CLIENT_COMPLETE);
    }
  }, [user]);

  useEffect(() => {
    setLoading(false);
  }, [location.pathname, location.state]);

  const stateTitle = location.state?.title;
  return (
    <GroupContext.Provider value={groupContextValue}>
      <ProgramsContext.Provider value={programsContextValue}>
        <CompactDateDialogContext.Provider
          value={compactDateDialogContextValue}
        >
          <FinishWorkoutModalContext.Provider
            value={finishWorkoutModalContextValue}
          >
            <MobileAppModalContext.Provider
              value={[isMobileAppModalShown, setIsMobileAppModalShown]}
            >
              <HistoryExerciseListModalContext.Provider
                value={exerciseListModalContextValue}
              >
                <AppLayout
                  title={
                    config.title === "..." || loading ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: 28,
                        }}
                      >
                        {stateTitle ?? (
                          <Skeleton variant="text" height={20} width={350} />
                        )}
                      </Box>
                    ) : (
                      config.title
                    )
                  }
                  subtitle={config.subtitle}
                  tabsNavigation={config.tabsNavigation}
                  actions={config.actions}
                  breadcrumbs={config.breadcrumbs}
                  disableSpeedDial={config.disableSpeedDial}
                  forceBackButton={config.forceBackButton}
                  appBar={config.appBar}
                  appDrawer={config.appDrawer}
                  appBarHideOnMobile={config.appBarHideOnMobile}
                >
                  <Outlet />
                </AppLayout>
              </HistoryExerciseListModalContext.Provider>
            </MobileAppModalContext.Provider>
          </FinishWorkoutModalContext.Provider>
        </CompactDateDialogContext.Provider>
      </ProgramsContext.Provider>
    </GroupContext.Provider>
  );
};

export default AppLayoutClient;
